import React, { useState, useEffect } from 'react';
import useAppStore from '../../store';

// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// CSS-STYLES
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
const TableStyles = () => (
    <style>{`
        .pe-tooltip-wrapper { position: relative; }
        .pe-tooltip-wrapper:hover::after {
            content: attr(data-tooltip);
            position: absolute;
            bottom: 100%; left: 50%; transform: translateX(-50%);
            background-color: #E6C299; color: #000000;
            padding: 8px 12px; border-radius: 4px;
            font-size: 0.75rem; font-weight: 700;
            white-space: normal; min-width: 220px; max-width: 300px;
            z-index: 2147483647 !important; pointer-events: none;
            box-shadow: 0 10px 25px rgba(0,0,0,0.8);
            margin-bottom: 12px; text-align: center;
            border: 2px solid #ffffff; line-height: 1.3;
        }
        .pe-tooltip-wrapper:hover::before {
            content: ''; position: absolute;
            bottom: 100%; left: 50%; margin-left: -6px;
            border-width: 6px; border-style: solid;
            border-color: #E6C299 transparent transparent transparent;
            margin-bottom: 0px; z-index: 2147483647 !important;
        }
        
        th.col-cm-date, td.col-cm-date { min-width: 180px !important; width: 180px !important; }
        th.col-cm-block, td.col-cm-block { min-width: 320px !important; width: 320px !important; vertical-align: top !important; white-space: normal !important; }
        td.col-expand { white-space: normal !important; min-width: 480px !important; }
        
        /* Input Gruppen */
        .cm-group { display: flex; flex-direction: column; gap: 0.65rem; padding: 0.35rem; }
        .input-wrapper.cm-input { height: 2.85rem !important; min-height: 2.85rem !important; }
        .client-master-table .table-input {
            text-align: left !important;
            padding-left: 0.65rem !important;
            padding-right: 0.65rem !important;
            font-size: 0.95rem !important;
        }
        .client-master-table .table-select {
            text-align: left !important;
            padding-left: 0.65rem !important;
            height: 2.85rem !important;
            line-height: 2.85rem !important;
            font-size: 0.95rem !important;
        }
        .client-master-table .table-textarea {
            min-height: 11rem !important;
            font-size: 0.95rem !important;
            line-height: 1.5 !important;
            padding: 0.75rem !important;
        }

        /* Ghosting */
        .ghost-value { color: #64748b !important; }
        .active-value { color: #ffffff !important; font-weight: 800; border-color: #94a3b8; }
        .input-error { border: 2px solid #ef4444 !important; background-color: rgba(239, 68, 68, 0.1); }

        /* ADMIN BADGE STYLE */
        .admin-badge { 
            background-color: rgba(6, 78, 59, 0.9); 
            border: 1px solid #10b981; 
            color: #d1fae5; 
            font-size: 0.65rem; 
            padding: 4px 2px; 
            border-radius: 4px; 
            display: flex; 
            flex-direction: column; 
            align-items: center; 
            justify-content: center;
            line-height: 1.1; 
            white-space: nowrap; 
            cursor: pointer; 
            transition: all 0.2s;
            width: 100%;
            height: 2.8rem; /* Match Row Height base */
            min-height: 100%;
        }
        .admin-badge:hover {
            background-color: #ef4444;
            border-color: #f87171;
            color: white;
        }
        .admin-badge:hover span { display: none; }
        .admin-badge:hover::after { content: 'UNLOCK'; font-weight: 800; }
    `}</style>
);

// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// 1. AKTIVE ZEILE
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

interface ActiveRowProps {
    itemId: string;
    onAdminLock: (id: string, isLocked: boolean) => void;
}

const ActiveClientMasterRow: React.FC<ActiveRowProps> = ({ itemId, onAdminLock }) => {
    const { clientData, updateClientMasterDraft, removeClientMasterDraft, commitDraftState } = useAppStore();
    const draft = clientData.drafts.clientMaster?.find(i => i.id === itemId);
    const original = clientData; // Root Level Properties

    const [localValues, setLocalValues] = useState<any>({});

    useEffect(() => {
        if (draft) {
            setLocalValues({ ...draft.inputs });
        }
    }, [draft]);

    if (!draft) return null;

    // HANDLER

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        const target = e.target;
        setTimeout(() => target.select(), 10);
    };

    const handleBlur = (field: string) => {
        commitDraftState();
        updateClientMasterDraft(itemId, field, localValues[field]);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        if (e.key === 'Enter') { 
            e.preventDefault(); 
            (e.target as HTMLElement).blur(); 
        }
    };

    const handleChange = (field: string, val: string) => setLocalValues((prev: any) => ({ ...prev, [field]: val }));

    const handleSelectChange = (field: string, val: string) => {
        commitDraftState();
        
        // Vorbelegung beim Datum-Setzen
        if (field === 'date' && val !== '') {
            // V1.1 FIX: Angepasst auf die korrekten Store-Keys (salonname & whatsappNr)
            const fields = ['unternehmername', 'salonname', 'unternehmensform', 'strasse', 'plz', 'ort', 'mailadresse', 'whatsappNr', 'websiteUrl', 'socialMedia'];
            fields.forEach(k => {
                const origVal = (original as any)[k] || '';
                updateClientMasterDraft(itemId, k, origVal);
            });
            // Lokal updaten damit es sofort angezeigt wird
            const newLocals = { ...localValues };
            fields.forEach(k => newLocals[k] = (original as any)[k] || '');
            newLocals.date = val;
            setLocalValues(newLocals);
        } else {
            handleChange(field, val);
        }
        
        updateClientMasterDraft(itemId, field, val);
    };

    // GHOSTING HELPER
    const getClass = (field: string) => {
        const val = localValues[field];
        const orig = (original as any)[field] || '';
        const isDiff = String(val).trim() !== String(orig).trim();
        return isDiff ? 'active-value' : 'ghost-value';
    };

    const isLocked = draft.isLocked;
    const rowClass = `pe-row active-row ${isLocked ? 'locked-row' : ''}`;

    return (
        <tr className={rowClass}>
            {/* RESET - ROTER MÜLLEIMER */}
            <td className="col-tight" style={{ width: '1px' }}>
                <div className="pe-tooltip-wrapper" data-tooltip="Änderung verwerfen / Löschen">
                    <button className="reset-btn-row" onClick={() => removeClientMasterDraft(itemId)} disabled={isLocked}>
                        <i className="fas fa-trash-alt" style={{ color: isLocked ? '#475569' : '#ef4444', fontSize: '0.8rem' }}></i>
                    </button>
                </div>
            </td>

            <td className="col-cm-date">
                <div className="pe-tooltip-wrapper" data-tooltip="Gültig ab">
                    <select 
                        className="table-select field-date input-active-look" 
                        value={localValues.date} 
                        onChange={(e) => handleSelectChange('date', e.target.value)}
                        disabled={isLocked}
                    >
                        {Array.from({length: 12}, (_, i) => { const d = `01.${String(i+1).padStart(2,'0')}.`; return <option key={d} value={d}>{d}</option> })}
                    </select>
                </div>
            </td>

            {/* BLOCK 1: BASIS */}
            <td className="col-cm-block">
                <div className="cm-group">
                    <div className="input-wrapper cm-input" data-tooltip="Unternehmername">
                        <input className={`table-input ${getClass('unternehmername')}`} value={localValues.unternehmername || ''} onChange={(e)=>handleChange('unternehmername', e.target.value)} onBlur={()=>handleBlur('unternehmername')} onFocus={handleFocus} onKeyDown={handleKeyDown} placeholder="Name" disabled={isLocked} />
                    </div>
                    {/* V1.1 FIX: salonname statt firmenname */}
                    <div className="input-wrapper cm-input" data-tooltip="Firmen/Salonname">
                        <input className={`table-input ${getClass('salonname')}`} value={localValues.salonname || ''} onChange={(e)=>handleChange('salonname', e.target.value)} onBlur={()=>handleBlur('salonname')} onFocus={handleFocus} onKeyDown={handleKeyDown} placeholder="Salon" disabled={isLocked} />
                    </div>
                    <select className={`table-select ${getClass('unternehmensform')}`} value={localValues.unternehmensform || ''} onChange={(e) => handleSelectChange('unternehmensform', e.target.value)} disabled={isLocked}>
                        <option value="" disabled>Rechtsform</option>
                        <option value="Einzelunternehmen">Einzelunternehmen</option>
                        <option value="GbR">GbR</option>
                        <option value="GmbH">GmbH</option>
                        <option value="UG">UG</option>
                        <option value="Sonstige">Sonstige</option>
                    </select>
                </div>
            </td>

            {/* BLOCK 2: ADRESSE */}
            <td className="col-cm-block">
                <div className="cm-group">
                    <div className="input-wrapper cm-input" data-tooltip="Straße & Hausnummer">
                        <input className={`table-input ${getClass('strasse')}`} value={localValues.strasse || ''} onChange={(e)=>handleChange('strasse', e.target.value)} onBlur={()=>handleBlur('strasse')} onFocus={handleFocus} onKeyDown={handleKeyDown} placeholder="Straße" disabled={isLocked} />
                    </div>
                    <div className="input-wrapper cm-input" data-tooltip="Postleitzahl">
                        <input className={`table-input ${getClass('plz')}`} value={localValues.plz || ''} onChange={(e)=>handleChange('plz', e.target.value)} onBlur={()=>handleBlur('plz')} onFocus={handleFocus} onKeyDown={handleKeyDown} placeholder="PLZ" disabled={isLocked} />
                    </div>
                    <div className="input-wrapper cm-input" data-tooltip="Ort">
                        <input className={`table-input ${getClass('ort')}`} value={localValues.ort || ''} onChange={(e)=>handleChange('ort', e.target.value)} onBlur={()=>handleBlur('ort')} onFocus={handleFocus} onKeyDown={handleKeyDown} placeholder="Ort" disabled={isLocked} />
                    </div>
                </div>
            </td>

            {/* BLOCK 3: KONTAKT */}
            <td className="col-cm-block">
                <div className="cm-group">
                    <div className="input-wrapper cm-input" data-tooltip="E-Mail Adresse">
                        <input className={`table-input ${getClass('mailadresse')}`} value={localValues.mailadresse || ''} onChange={(e)=>handleChange('mailadresse', e.target.value)} onBlur={()=>handleBlur('mailadresse')} onFocus={handleFocus} onKeyDown={handleKeyDown} placeholder="E-Mail" disabled={isLocked} />
                    </div>
                    {/* V1.1 FIX: whatsappNr statt telefonnummer */}
                    <div className="input-wrapper cm-input" data-tooltip="Telefon / WhatsApp">
                        <input className={`table-input ${getClass('whatsappNr')}`} value={localValues.whatsappNr || ''} onChange={(e)=>handleChange('whatsappNr', e.target.value)} onBlur={()=>handleBlur('whatsappNr')} onFocus={handleFocus} onKeyDown={handleKeyDown} placeholder="Tel/WhatsApp" disabled={isLocked} />
                    </div>
                    <div className="input-wrapper cm-input" data-tooltip="Webseite">
                        <input className={`table-input ${getClass('websiteUrl')}`} value={localValues.websiteUrl || ''} onChange={(e)=>handleChange('websiteUrl', e.target.value)} onBlur={()=>handleBlur('websiteUrl')} onFocus={handleFocus} onKeyDown={handleKeyDown} placeholder="Webseite" disabled={isLocked} />
                    </div>
                    <div className="input-wrapper cm-input" data-tooltip="Social Media (Insta/FB)">
                        <input className={`table-input ${getClass('socialMedia')}`} value={localValues.socialMedia || ''} onChange={(e)=>handleChange('socialMedia', e.target.value)} onBlur={()=>handleBlur('socialMedia')} onFocus={handleFocus} onKeyDown={handleKeyDown} placeholder="Social Media" disabled={isLocked} />
                    </div>
                </div>
            </td>

            {/* NOTIZEN */}
            <td className="col-expand">
                <textarea className="table-textarea h-full min-h-[11rem]" value={localValues.notes} onChange={(e)=>handleChange('notes', e.target.value)} onBlur={()=>handleBlur('notes')} onKeyDown={handleKeyDown} placeholder="Notizen..." disabled={isLocked}></textarea>
            </td>
            
            {/* ADMIN SPERRE */}
            <td className="col-tight">
                {isLocked ? (
                    <div className="admin-badge" onClick={() => onAdminLock(itemId, true)}>
                        <span className="text-lg font-bold">{draft.lockedBy}</span>
                        <span style={{ fontSize: '0.55rem' }}>{draft.lockedAt}</span>
                    </div>
                ) : (
                    <button className="btn-admin-lock" onClick={() => onAdminLock(itemId, false)}>
                        <i className="fas fa-lock"></i>
                    </button>
                )}
            </td>
        </tr>
    );
};

// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// 2. JOKER ZEILE
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

const JokerClientMasterRow: React.FC = () => {
    const { addClientMasterDraft, commitDraftState, updateClientMasterDraft, clientData } = useAppStore();
    const [selectedDate, setSelectedDate] = useState('');
    const original = clientData;

    const handleDateSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const date = e.target.value;
        setSelectedDate(date);
        
        if (date) {
            commitDraftState();
            addClientMasterDraft();
            setTimeout(() => {
                const drafts = useAppStore.getState().clientData.drafts.clientMaster;
                if (drafts && drafts.length > 0) {
                    const lastItem = drafts[drafts.length - 1];
                    updateClientMasterDraft(lastItem.id, 'date', date);
                    
                    // AUTO-FILL - V1.1 FIX: Die korrekten Store-Keys hinterlegt
                    const fields = ['unternehmername', 'salonname', 'unternehmensform', 'strasse', 'plz', 'ort', 'mailadresse', 'whatsappNr', 'websiteUrl', 'socialMedia'];
                    fields.forEach(k => {
                        const val = (original as any)[k] || '';
                        updateClientMasterDraft(lastItem.id, k, val);
                    });
                }
            }, 0);
            setSelectedDate('');
        }
    };

    return (
        <tr className="pe-row">
            {/* RESET - PLUS SYMBOL FÜR NEUE ZEILE */}
            <td className="col-tight"><button className="reset-btn-row" disabled><i className="fas fa-plus text-stone-300 dark:text-slate-700"></i></button></td>
            
            <td className="col-cm-date">
                <div className="pe-tooltip-wrapper" data-tooltip="Datum wählen für Änderung">
                    <select className={`table-select field-date ${!selectedDate ? 'border-blue-500/50' : ''}`} value={selectedDate} onChange={handleDateSelect}>
                        <option value="">Startdatum wählen</option>
                        {Array.from({length: 12}, (_, i) => { const d = `01.${String(i+1).padStart(2,'0')}.`; return <option key={d} value={d}>{d}</option> })}
                    </select>
                </div>
            </td>
            
            <td className="col-cm-block">
                <div className="cm-group">
                    <div className="input-wrapper cm-input"><input className="table-input ghost-value" disabled placeholder={original.unternehmername || 'Unternehmername'} /></div>
                    {/* V1.1 FIX: salonname statt firmenname für Ghosting */}
                    <div className="input-wrapper cm-input"><input className="table-input ghost-value" disabled placeholder={original.salonname || 'Salon'} /></div>
                    <div className="input-wrapper cm-input"><input className="table-input ghost-value" disabled placeholder={original.unternehmensform || 'Rechtsform'} /></div>
                </div>
            </td>
            
            <td className="col-cm-block">
                <div className="cm-group">
                    <div className="input-wrapper cm-input"><input className="table-input ghost-value" disabled placeholder={original.strasse || 'Straße'} /></div>
                    <div className="input-wrapper cm-input"><input className="table-input ghost-value" disabled placeholder={original.plz || 'PLZ'} /></div>
                    <div className="input-wrapper cm-input"><input className="table-input ghost-value" disabled placeholder={original.ort || 'Ort'} /></div>
                </div>
            </td>

            <td className="col-cm-block">
                <div className="cm-group">
                    <div className="input-wrapper cm-input"><input className="table-input ghost-value" disabled placeholder={original.mailadresse || 'E-Mail'} /></div>
                    {/* V1.1 FIX: whatsappNr statt telefonnummer für Ghosting */}
                    <div className="input-wrapper cm-input"><input className="table-input ghost-value" disabled placeholder={original.whatsappNr || 'Tel/WhatsApp'} /></div>
                    <div className="input-wrapper cm-input"><input className="table-input ghost-value" disabled placeholder={original.websiteUrl || 'Webseite'} /></div>
                    <div className="input-wrapper cm-input"><input className="table-input ghost-value" disabled placeholder={original.socialMedia || 'Social Media'} /></div>
                </div>
            </td>

            <td className="col-expand"><textarea className="table-textarea" disabled placeholder="Notizen..."></textarea></td>
            <td><button className="btn-admin-lock" disabled><i className="fas fa-lock"></i></button></td>
        </tr>
    );
};

// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// HAUPT-KOMPONENTE
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

interface ClientMasterTableProps {
    onAdminLock: (id: string, isLocked: boolean) => void;
}

const ClientMasterTable: React.FC<ClientMasterTableProps> = ({ onAdminLock }) => {
    const { clientData } = useAppStore();
    const drafts = clientData.drafts.clientMaster || [];

    return (
        <div className="table-wrapper">
            <TableStyles />
            <table className="text-left border-collapse client-master-table">
                <thead className="pe-table-header">
                    <tr>
                        <th className="col-tight px-2 min-w-[50px]">Reset</th>
                        <th className="col-cm-date px-2">Gültig ab</th>
                        <th className="col-cm-block px-2">Basis-Info</th>
                        <th className="col-cm-block px-2">Adresse</th>
                        <th className="col-cm-block px-2">Kontakt</th>
                        <th className="col-expand px-2 min-w-[480px]">Info / Notizen</th>
                        <th className="col-tight px-2 min-w-[80px]">Admin</th>
                    </tr>
                </thead>
                <tbody id="clientMasterBody">
                    {drafts.map(item => (
                        <ActiveClientMasterRow key={item.id} itemId={item.id} onAdminLock={onAdminLock} />
                    ))}
                    <JokerClientMasterRow />
                </tbody>
            </table>
        </div>
    );
};

export default ClientMasterTable;